import { Alignment, Fit, Layout, Rive } from '@rive-app/canvas';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// One .riv fish, on a canvas. The same five files the phone ships, unchanged.
//
// ⚠ THE CONTRACT IS THE ART'S, NOT OURS. app/home/FishItem.tsx verified these
// names against each binary's string table:
//   Focused  (boolean)  the "selected / at the front" pose  → we drive it on hover
//   Selected (trigger)  the tapped animation                → we fire it on click
// A re-export under different names breaks both clients the same way.
// ─────────────────────────────────────────────────────────────────────────────

export const ARTBOARD = 'Artboard';
export const STATE_MACHINE = 'State Machine 1';
export const BOOL_INPUT = 'Focused';
export const TRIGGER_INPUT = 'Selected';

export type FishHandle = { fireSelected: () => void };

type Props = {
  /** Path under /fish, e.g. "story_fish.riv". */
  src: string;
  focused: boolean;
  size: number;
};

const FishRive = forwardRef<FishHandle, Props>(function FishRive({ src, focused, size }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const riveRef = useRef<Rive | null>(null);
  // Read inside onLoad without making `focused` a dependency of the load effect —
  // reloading the .riv on every hover would restart it from frame zero.
  const focusedRef = useRef(focused);
  focusedRef.current = focused;

  useEffect(() => {
    if (!canvasRef.current) return;
    const rive = new Rive({
      src: `/fish/${src}`,
      canvas: canvasRef.current,
      artboard: ARTBOARD,
      stateMachines: STATE_MACHINE,
      autoplay: true,
      layout: new Layout({ fit: Fit.Contain, alignment: Alignment.Center }),
      onLoad: () => {
        rive.resizeDrawingSurfaceToCanvas();
        setFocused(rive, focusedRef.current);
      },
      onLoadError: (e: unknown) => console.error(`[FishRive] ${src} failed to load:`, e),
    });
    riveRef.current = rive;
    return () => { rive.cleanup(); riveRef.current = null; };
  }, [src]);

  useEffect(() => {
    if (riveRef.current) setFocused(riveRef.current, focused);
  }, [focused]);

  // The drawing surface is sized in device pixels; keep it in step with layout.
  useEffect(() => {
    riveRef.current?.resizeDrawingSurfaceToCanvas();
  }, [size]);

  useImperativeHandle(ref, () => ({
    fireSelected: () => {
      try {
        const input = riveRef.current?.stateMachineInputs(STATE_MACHINE)
          ?.find(i => i.name === TRIGGER_INPUT);
        input?.fire();
      } catch (e) {
        console.error(`[FishRive] firing ${TRIGGER_INPUT} threw:`, e);
      }
    },
  }), []);

  return (
    <canvas
      ref={canvasRef}
      width={size * 2}
      height={size * 2}
      style={{ width: size, height: size, display: 'block' }}
    />
  );
});

export default FishRive;

/** Tolerates the window before the state machine has instantiated its inputs. */
function setFocused(rive: Rive, focused: boolean) {
  try {
    const input = rive.stateMachineInputs(STATE_MACHINE)?.find(i => i.name === BOOL_INPUT);
    if (input) input.value = focused;
  } catch (e) {
    console.error(`[FishRive] setting ${BOOL_INPUT} threw:`, e);
  }
}
